import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createAppTheme } from './createAppTheme';
import {
    ColorSchemeId,
    COLOR_SCHEMES,
    DEFAULT_COLOR_SCHEME,
    getColorScheme,
} from './colorSchemes';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
    mode: ThemeMode;
    setMode: (mode: ThemeMode) => void;
    isDark: boolean;
    colorScheme: ColorSchemeId;
    setColorScheme: (scheme: ColorSchemeId) => void;
    availableSchemes: typeof COLOR_SCHEMES;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const MODE_STORAGE_KEY = 'theme-mode';
const SCHEME_STORAGE_KEY = 'theme-color-scheme';

export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mode, setModeState] = useState<ThemeMode>(() => {
        const stored = localStorage.getItem(MODE_STORAGE_KEY);
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
            return stored;
        }
        return 'system';
    });

    const [colorScheme, setColorSchemeState] = useState<ColorSchemeId>(() => {
        const stored = localStorage.getItem(SCHEME_STORAGE_KEY);
        if (stored && stored in COLOR_SCHEMES) {
            return stored as ColorSchemeId;
        }
        return DEFAULT_COLOR_SCHEME;
    });

    const [systemPrefersDark, setSystemPrefersDark] = useState(() =>
        window.matchMedia('(prefers-color-scheme: dark)').matches
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);

        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, []);

    const setMode = (newMode: ThemeMode) => {
        setModeState(newMode);
        localStorage.setItem(MODE_STORAGE_KEY, newMode);
    };

    const setColorScheme = (newScheme: ColorSchemeId) => {
        setColorSchemeState(newScheme);
        localStorage.setItem(SCHEME_STORAGE_KEY, newScheme);
    };

    const isDark = mode === 'dark' || (mode === 'system' && systemPrefersDark);

    const theme = useMemo(() => {
        const scheme = getColorScheme(colorScheme);
        return createAppTheme(scheme, isDark);
    }, [colorScheme, isDark]);

    const contextValue = useMemo(() => ({
        mode,
        setMode,
        isDark,
        colorScheme,
        setColorScheme,
        availableSchemes: COLOR_SCHEMES,
    }), [mode, isDark, colorScheme]);

    return (
        <ThemeContext.Provider value={contextValue}>
            <MuiThemeProvider theme={theme}>
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useThemeMode = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useThemeMode must be used within ThemeContextProvider');
    }
    return context;
};
